/**
 * Visual Identity (§H) — EXPLICIT-SAVE conversion (bb-phase2-explicit-save-
 * brief-20260810 + bb-save-ux-slate-brief-20260810).
 *
 * Six fields only (§H contract, do not add a seventh):
 *  1. logo_refs         — repeatable rows: kind + reference pointer + usage note.
 *  2. color_tokens      — role-keyed object, exactly six roles, hex values.
 *  3. font_names        — chip list of plain font-name strings.
 *  4. photo_library_ref — one pointer text field (library management lives in
 *                         Image Assets, not here).
 *  5. real_photos_only  — DERIVED (read-only), NOT hand-editable: the tenant
 *                         Image Source setting has the final say. Only "client's
 *                         own assets" makes it true; AI/stock/hybrid are false.
 *                         Still stored + round-tripped (a control was removed,
 *                         not the field); see the §H derivation block below.
 *  6. badge_usage_notes — the only prose field in §H.
 *
 * POINTERS ONLY: reference fields are plain text (URL or asset pointer) — this
 * panel never renders a file-upload control.
 *
 * ─── BUFFERING (this conversion) ───────────────────────────────────────────
 * Field EDITS no longer write on blur/change. They accumulate in a section-
 * local `buffer` (sparse — a field appears in it only while it differs from the
 * store). The registered `save()` commits the buffered diff through the SAME
 * services the panel already used (`useSingleton('visual_identity').save`
 * = upsertEntry, plus the page's `useBbDraftCommit()` seam for the one flat
 * legacy column). `discard()` drops the buffer back to store state.
 * `changeCount()` is a real per-item tally (edited colour roles + font set
 * delta + logo-row delta + edited scalar fields), never a boolean cast to 1.
 * The panel registers with `useRegisterBbSectionSave('H', …)`.
 *
 * WHAT STAYS IMMEDIATE (operator acts, not field edits): Verify, Unverify, and
 * the "Approved for public use" checkbox all write instantly through their own
 * store actions — they are governance, never buffered. real_photos_only's
 * derivation/reconciliation is a SYSTEM write (following the image source), not
 * an operator edit, so it too stays immediate.
 *
 * HAZARD NOTES honoured here:
 *  - logo_refs is buffered as the RAW store array with only the edited row
 *    replaced (spread over the original object), so an untouched enrichment
 *    row round-trips byte-identical (the display misread of {name,role,media_id}
 *    rows is a SEPARATE issue tracked on #1624 — buffering neither fixes nor
 *    worsens it).
 *  - SmartTagInput's blur-flush now lands in the buffer, not the service, so
 *    tabbing away from a half-typed font no longer writes to production.
 *  - color_tokens is committed as {…currentStore, …editedRoles}: unknown roles
 *    the client stored under non-contract keys (light, blue_gray, …) survive
 *    byte-identical because only edited roles are overlaid.
 *  - Only edited fields enter the save patch (snapshot-then-diff) — an
 *    untouched field is never re-sent, so a stale whole-JSONB PUT can't clobber
 *    a concurrent write.
 *
 * Same singleton-table pattern as VoiceProfilePanel (one row per tenant,
 * location_scope='all', `useSingleton('visual_identity')`); same Core NAMED
 * tokens and wizard primitives as ChannelRulesPanel/VoiceProfilePanel.
 *
 * A 7th card — "Imagery & references" — renders below the six §H fields. That
 * is NOT a bb_visual_identity column — it's the legacy flat
 * `brand_bible.visual_identity` column, which has no v2 home. It buffers with
 * everything else and, on save, commits through the page's `useBbDraftCommit()`
 * seam. If that seam is unavailable, `save()` FAILS LOUDLY rather than dropping
 * the buffered imagery silently. `legacyImageryValue` supplies its display base
 * (the page owns the column's value); on `main` the page mounts this panel
 * without that prop, so the base is '' — see the panel's PR notes.
 */
import { Component, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTenantSettings } from '@/hooks/useTenantSettings';
import { useBrandBibleWizardStore } from '@/stores/brandBibleWizardStore';
import { useSingleton } from '@/components/brand-bible-wizard/steps/useSingleton';
import { SmartTagInput } from './SmartTagInput';
import { AISuggest } from './AISuggest';
import {
  EnvelopeBadge,
  Field,
  StepCard,
  inputClass,
} from '@/components/brand-bible-wizard/primitives';
import type { DirtyFormSaveResult } from '@/components/settings/DirtyFormGuard';
import { useBbDraftCommit, useRegisterBbSectionSave } from './save';
import type { BbSectionSaveRegistration } from './save';
import {
  ASSETS_POINTER_NOTE,
  COLOR_ROLES,
  H_SECTION_IDS,
  MISS_MSG,
  MISSING_TENANT_MSG,
  OUTPUT_ERROR_MSG,
  PROVIDER_ERROR_MSG,
  REAL_PHOTOS_NO_SUGGEST,
  SUGGEST_SOURCE,
  buildAssetsRequest,
  buildBadgeRequest,
  buildStylingRequest,
  classifySuggestError,
  emptyColorRoles,
  fetchHSuggestions,
  itemFromAssetsDraft,
  itemFromBadgeDraft,
  itemFromStylingDraft,
  isValidHexToken,
  recordedHexes,
  recordedLogoPointers,
  type HSuggestItem,
} from './visualIdentityAiSuggest';
import { requestModelFieldSuggestions } from '@/services/brandBibleAiSuggest';
import type { FieldDraft, FieldRefusal } from '@/services/brandBibleAiSuggest';
import type {
  BbEntry,
  BbTable,
  ColorTokenRole,
  ColorTokens,
  LogoRef,
  LogoRefKind,
  VisualIdentityPayload,
} from '@/features/brand-bible-wizard/types';

/**
 * Header "Verify all" affordance (v2 fold-in — bb-verify-all-header-brief-
 * 20260810). §H's own record is a singleton, so this ever verifies at most
 * one row — the button still shows at N=1, it's the SAME contract as B/C/D,
 * just with a smaller N. Rank-2 outline style (never filled), hidden when
 * there's nothing pending or the viewer can't verify (§4.3). Verify stays
 * IMMEDIATE — it is an operator act, never a buffered edit.
 */
type VerifyAllItem = { table: BbTable; id: string };
type VerifyAllPhase = 'idle' | 'running' | 'success' | 'partial';

function VerifyAllButton({
  items,
  canVerify,
  verifyEntry,
  tenantId,
}: {
  items: VerifyAllItem[];
  canVerify: boolean;
  verifyEntry: (table: BbTable, id: string, approveForPublic: boolean | undefined, tenantId: string) => Promise<void>;
  /** The tenant this button rendered for (a stale run is refused by the store). */
  tenantId: string;
}) {
  const [phase, setPhase] = useState<VerifyAllPhase>('idle');
  const [result, setResult] = useState<{ verified: number; failed: number } | null>(null);
  const pendingCount = items.length;

  useEffect(() => {
    if (phase === 'success' && pendingCount > 0) {
      setPhase('idle');
      setResult(null);
    }
  }, [phase, pendingCount]);

  if (!canVerify) return null;
  if (phase === 'idle' && pendingCount === 0) return null;

  const runAll = async () => {
    setPhase('running');
    let verified = 0;
    let failed = 0;
    for (const item of items) {
      try {
        await verifyEntry(item.table, item.id, undefined, tenantId);
        verified += 1;
      } catch {
        failed += 1;
      }
    }
    setResult({ verified, failed });
    setPhase(failed > 0 ? 'partial' : 'success');
  };

  if (phase === 'success') {
    return <span className="text-xs font-medium text-[hsl(var(--status-success))]">✓ All verified</span>;
  }

  if (phase === 'partial' && result) {
    return (
      <button
        type="button"
        onClick={() => void runAll()}
        className="rounded-md border border-destructive px-2.5 py-1 text-xs font-semibold text-destructive hover:bg-destructive/10"
      >
        {result.verified} verified · {result.failed} failed — retry
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void runAll()}
      disabled={phase === 'running'}
      className="rounded-md border border-coral-dark bg-warm-bg px-2.5 py-1 text-xs font-semibold text-coral-dark hover:bg-coral-light disabled:cursor-not-allowed disabled:opacity-60"
    >
      {phase === 'running' ? 'Verifying…' : `✓ Verify all · ${pendingCount} pending`}
    </button>
  );
}

export interface VisualIdentityPanelProps {
  canVerify: boolean;
  /**
   * Display base for the legacy "Imagery & references" flat column
   * (`brand_bible.visual_identity`). The page owns this column's value; the
   * panel buffers edits on top of this base and commits them on save through
   * the `useBbDraftCommit()` seam. Defaults to '' so every standalone render
   * (tests; the current `main` page mount, which passes only `canVerify`) still
   * works — the card simply starts from an empty base.
   */
  legacyImageryValue?: string;
}

/**
 * The panel body takes the resolved tenant image source as a prop rather than
 * reading it itself. That read (useTenantSettings → useAuth) is lifted into the
 * wrapper below so the body's hooks stay provider-independent; `null` means the
 * source is UNKNOWN (not loaded, absent, or no AuthProvider to read it).
 */
interface VisualIdentityPanelBodyProps extends VisualIdentityPanelProps {
  imageSource: string | null;
}

/* ---------------------------------- helpers ---------------------------------- */

const LOGO_KIND_OPTIONS: { value: LogoRefKind; label: string }[] = [
  { value: 'primary', label: 'Primary' },
  { value: 'wordmark', label: 'Wordmark' },
  { value: 'mark', label: 'Mark' },
  { value: 'favicon', label: 'Favicon' },
  { value: 'other', label: 'Other' },
];

const isValidHex = isValidHexToken;

const asLogoRefs = (v: unknown): LogoRef[] => (Array.isArray(v) ? (v as LogoRef[]) : []);
const asColorTokens = (v: unknown): Partial<ColorTokens> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Partial<ColorTokens>) : {};
const asFontNames = (v: unknown): string[] => (Array.isArray(v) ? (v as string[]) : []);

/* ---------------------- enrichment-shaped logo_refs compat --------------------- */
/**
 * Compatibility for enrichment-shaped logo_refs rows
 * (SECTION-H-L3-BLOCK-logo-refs-shape-20260815, RULING from %662; PR #1624).
 *
 * Production `bb_visual_identity.logo_refs` rows written by ENRICHMENT carry the
 * shape `{ name, role, media_id }` — they have NO `kind`, `reference`, or
 * `usage_note` key. The canonical §H editor contract is
 * `LogoRef {kind, reference, usage_note?}`. `asLogoRefs` above is a blind CAST,
 * not a parse, so these rows would otherwise render as blank, editable PRIMARY
 * rows — and the natural operator cleanup (deleting the "empty" rows, or typing
 * into them) would DESTROY stored references the editor never understood.
 *
 * The rule here is compatibility ONLY: DETECT these rows, render them READ-ONLY
 * ("enrichment (unmigrated)") as truth, EXCLUDE them from every editable row
 * action, and preserve them BYTE-IDENTICAL on save. Under buffering they are
 * never in an editable action's write path, so a buffered Save carries them
 * through unchanged — the byte-identical guarantee holds through buffer → save.
 */
interface EnrichmentLogoRef {
  name?: unknown;
  role?: unknown;
  media_id?: unknown;
}

/**
 * A row is enrichment-shaped when it lacks BOTH canonical keys (`kind`,
 * `reference`) yet carries at least one enrichment key (`name`, `role`,
 * `media_id`). This is presence-of-KEY, never truthiness: a canonical row with
 * `reference: ''` still OWNS the `reference` key and is never mistaken for an
 * enrichment row. Any other odd/degraded shape (a bare string, `{}`, a partial
 * canonical row) is deliberately NOT enrichment — it flows to the guarded
 * editable branch and degrades gracefully there.
 */
function isEnrichmentLogoRef(row: unknown): boolean {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return false;
  const r = row as Record<string, unknown>;
  const hasCanonicalKey = 'kind' in r || 'reference' in r;
  const hasEnrichmentKey = 'name' in r || 'role' in r || 'media_id' in r;
  return hasEnrichmentKey && !hasCanonicalKey;
}

/** Read-only display projection of an enrichment row (never mutates the row). */
function enrichmentDisplay(row: unknown): { name: string; role: string; mediaId: string } {
  const r = (row ?? {}) as EnrichmentLogoRef;
  const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
  return { name: str(r.name), role: str(r.role), mediaId: str(r.media_id) };
}

/* ----------------------------- buffer bookkeeping ----------------------------- */

/**
 * The section-local buffer: a field is present ONLY while its buffered value
 * differs from the store (setters prune a no-op edit back out), so
 * `Object.keys(buffer).length > 0` ⟺ dirty and every present field is a real
 * change. `colorEdits` is a per-ROLE map (not the whole object) precisely so a
 * save merges edited roles over the current store value and leaves every
 * unknown/non-contract role byte-identical.
 */
interface HBuffer {
  logoRefs?: LogoRef[];
  colorEdits?: Partial<Record<ColorTokenRole, string>>;
  fontNames?: string[];
  photoLibraryRef?: string;
  badgeUsageNotes?: string;
  imagery?: string;
}

/** Set-equality on string lists (order-insensitive) — font names are a set. */
const sameStringSet = (a: readonly string[], b: readonly string[]): boolean => {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((x, i) => x === sb[i]);
};

/** Added + removed between two string sets. */
const stringSetDelta = (a: readonly string[], b: readonly string[]): number => {
  const as = new Set(a);
  const bs = new Set(b);
  let n = 0;
  as.forEach((x) => {
    if (!bs.has(x)) n += 1;
  });
  bs.forEach((x) => {
    if (!as.has(x)) n += 1;
  });
  return n;
};

const logoRowsEqual = (a: readonly LogoRef[], b: readonly LogoRef[]): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

/** Added + removed + per-index modified rows. */
const logoRowDelta = (buf: readonly LogoRef[], store: readonly LogoRef[]): number => {
  const m = Math.min(buf.length, store.length);
  let n = Math.abs(buf.length - store.length);
  for (let i = 0; i < m; i += 1) {
    if (JSON.stringify(buf[i]) !== JSON.stringify(store[i])) n += 1;
  }
  return n;
};

interface CountSnapshot {
  logoRefs: readonly LogoRef[];
  fontNames: readonly string[];
}

/** Honest per-item change tally — never a boolean cast to 1. */
function computeChangeCount(buffer: HBuffer, snap: CountSnapshot): number {
  let n = 0;
  if (buffer.colorEdits) n += Object.keys(buffer.colorEdits).length;
  if (buffer.fontNames) n += stringSetDelta(buffer.fontNames, snap.fontNames);
  if (buffer.logoRefs) n += logoRowDelta(buffer.logoRefs, snap.logoRefs);
  if (buffer.photoLibraryRef !== undefined) n += 1;
  if (buffer.badgeUsageNotes !== undefined) n += 1;
  if (buffer.imagery !== undefined) n += 1;
  return n;
}

/* --------------------- §H field-coverage AI-suggest controls --------------------- */
/**
 * Three buttons, ceiling of three. Each posts the structured A–I contract
 * (section H, requestModelFieldSuggestions) — never aiSuggestService.
 *   visual-assets       → logo_refs, photo_library_ref (grounded)
 *   visual-styling      → color_tokens, font_names (grounded)
 *   visual-badge-notes  → badge_usage_notes (creative, unverified draft)
 * real_photos_only is derived/read-only and is not a suggest target.
 *
 * Routing uses output_key/target_field, never a parse of chip text.
 * Invented hex/font/path values are dropped client-side even if the model
 * returns them. Completeness nudges (empty-row / focus) are plain UI, not AI.
 * An accepted suggestion is a buffered EDIT, exactly like a keystroke — it is
 * never written until Save.
 */
type SuggestErrorKind = 'provider' | 'output' | 'tenant' | null;
type SuggestUi = { refusals: FieldRefusal[]; errorKind: SuggestErrorKind; miss: boolean };
const EMPTY_SUGGEST_UI: SuggestUi = { refusals: [], errorKind: null, miss: false };

function errorCopy(kind: SuggestErrorKind): string {
  if (kind === 'output') return OUTPUT_ERROR_MSG;
  if (kind === 'tenant') return MISSING_TENANT_MSG;
  return PROVIDER_ERROR_MSG;
}

function HSuggestChrome({
  notes,
  ui,
}: {
  notes?: string[];
  ui: SuggestUi;
}) {
  return (
    <>
      {(notes ?? []).map((note) => (
        <p key={note} className="max-w-[480px] text-right text-[10.5px] leading-snug text-muted-foreground">
          {note}
        </p>
      ))}
      {ui.refusals.map((r) => (
        <p
          key={`${r.output_key}:${r.target_field}:${r.reason}`}
          className="max-w-[480px] text-right text-[10.5px] leading-snug text-muted-foreground"
        >
          {r.target_field} · {r.reason}
        </p>
      ))}
      {ui.errorKind && (
        <p role="alert" className="text-right text-[11px] text-destructive">
          {errorCopy(ui.errorKind)}
        </p>
      )}
      {ui.miss && (
        <p role="alert" className="text-[11px] text-destructive">
          {MISS_MSG}
        </p>
      )}
    </>
  );
}

function verifiedMonthYear(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/* -------------------------------- Record rail --------------------------------- */

function RecordRail({
  entry,
  canVerify,
  onVerify,
  onUnverify,
  onApprovalChange,
}: {
  entry: BbEntry<'visual_identity'>;
  canVerify: boolean;
  onVerify: () => void;
  onUnverify: () => void;
  onApprovalChange: (checked: boolean) => void;
}) {
  const verifiedLabel = entry.verified_by ? verifiedMonthYear(entry.verified_at) : null;
  const isVerified = Boolean(entry.verified_by);

  // Provenance pills are GREY, never amber: an unverified record is a state,
  // not an alarm. Warning colour stays reserved for something the operator has
  // to act on. White-fill bordered pills read clearly on the quiet grey band.
  const provenancePill =
    'inline-flex items-center rounded-[5px] border border-border bg-warm-bg px-1.5 py-0.5 ' +
    'font-mono text-[10px] font-semibold uppercase tracking-wide text-teal-dark';

  return (
    <div className="mb-3.5 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-teal-light px-3.5 py-2.5">
      <span className={provenancePill}>{entry.source}</span>
      <span className={provenancePill}>confidence: {entry.confidence}</span>
      <span className="text-[11px] text-muted-foreground">scope: {entry.location_scope}</span>
      {isVerified ? (
        <EnvelopeBadge kind="verified">Verified{verifiedLabel ? ` · ${verifiedLabel}` : ''}</EnvelopeBadge>
      ) : (
        <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
          Pending verification
        </span>
      )}

      {/* Right end: the Verify control (or Unverify once verified) and the
          public-use control both live here. All three stay IMMEDIATE. */}
      <div className="ml-auto flex items-center gap-2.5">
        {canVerify &&
          (isVerified ? (
            <button
              type="button"
              onClick={onUnverify}
              className="text-[11px] font-medium text-muted-foreground underline-offset-2 hover:text-coral-dark hover:underline"
            >
              Unverify
            </button>
          ) : (
            <button
              type="button"
              onClick={onVerify}
              className="text-[11px] font-semibold text-coral hover:text-coral-hover"
            >
              Verify
            </button>
          ))}
        <label
          className={cn(
            'flex items-center gap-1.5 text-[11px]',
            isVerified ? 'text-teal-dark' : 'cursor-not-allowed text-muted-foreground opacity-60',
          )}
          title={isVerified ? undefined : 'Verify first to allow public use'}
        >
          <input
            type="checkbox"
            checked={!!entry.approved_for_public}
            disabled={!isVerified}
            onChange={(e) => onApprovalChange(e.target.checked)}
            className="accent-coral disabled:cursor-not-allowed"
          />
          Approved for public use
        </label>
      </div>
    </div>
  );
}

/* --------------------------------- Color role chip ----------------------------- */

function ColorRoleChip({
  role,
  label,
  value,
  error,
  onCommit,
}: {
  role: ColorTokenRole;
  label: string;
  value: string;
  error?: string;
  onCommit: (role: ColorTokenRole, raw: string) => void;
}) {
  const isSet = isValidHex(value);
  return (
    <div className="flex flex-col gap-1">
      <div
        className={cn(
          'inline-flex items-center gap-2 rounded-full border bg-card py-1 pl-1.5 pr-3',
          error ? 'border-destructive' : 'border-border',
        )}
      >
        {isSet ? (
          // A set role is a solid filled swatch. The ONLY legitimate raw color
          // is the tenant's own stored hex, rendered from data.
          <span
            aria-hidden
            className="h-6 w-6 flex-none rounded-full border border-border"
            style={{ backgroundColor: value.trim() }}
          />
        ) : (
          // An unset swatch must NOT read as an unchecked checkbox: a round,
          // dashed, diagonally-slashed disc is unmistakably "empty" — different
          // in kind from a filled disc, not merely a different colour.
          <span
            aria-hidden
            className="flex h-6 w-6 flex-none items-center justify-center rounded-full border border-dashed border-input text-muted-foreground/50"
          >
            <svg viewBox="0 0 24 24" className="h-full w-full" aria-hidden>
              <line x1="6" y1="18" x2="18" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </span>
        )}
        <span className="flex flex-col leading-tight">
          <span className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
          <input
            className="w-[84px] bg-transparent font-mono text-[12.5px] uppercase text-foreground outline-none placeholder:text-muted-foreground/50"
            defaultValue={value}
            placeholder="#RRGGBB"
            aria-label={`${label} color hex`}
            onBlur={(e) => onCommit(role, e.target.value)}
          />
        </span>
      </div>
      {error && (
        <p role="alert" className="text-[11px] text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

/* -------------------------- No-generated-value note ---------------------------- */

/**
 * Fix 3 — refusal state. A covered fact field (colour, fonts) uses this quiet
 * note to say WHY generation offers nothing here: the value has to come from the
 * client, and the suggestion only opens the field — it never invents one. So an
 * empty suggestion list reads as an intentional choice, not a fetch failure.
 * Deliberately muted (a grey dot + muted text, the file's existing quiet style)
 * and visibly DISTINCT from the destructive/red styling used for save and
 * validation errors.
 */
function NoGeneratedValueNote({ children }: { children: ReactNode }) {
  return (
    <p className="mt-2.5 flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
      <span aria-hidden className="mt-[3px] h-1.5 w-1.5 flex-none rounded-full bg-muted-foreground/40" />
      <span>{children}</span>
    </p>
  );
}

/* --------------------- enrichment-shaped logo row (read-only) ------------------- */

/**
 * READ-ONLY presentation of an enrichment-shaped logo_refs row (PR #1624). It
 * shows the stored data as truth — `name` as the label, `role` as the usage
 * note, and `media #<id>` as the pointer surrogate — and is labelled
 * "enrichment (unmigrated)". It renders NO editable control and NO remove
 * button, so it can never be mutated, reshaped, or deleted through the editor.
 * The stored object is preserved byte-identical through the buffer to save
 * because it is never in an editable action's write path.
 */
function EnrichmentLogoRow({ index, row }: { index: number; row: unknown }) {
  const { name, role, mediaId } = enrichmentDisplay(row);
  return (
    <div
      data-testid={`logo-enrichment-row-${index}`}
      className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-input bg-teal-light px-2.5 py-1.5"
    >
      <span className="flex-none rounded-full border border-border bg-warm-bg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-teal-dark">
        enrichment (unmigrated)
      </span>
      <span className="min-w-0 flex-1 truncate font-mono text-[12.5px] text-foreground" title={name}>
        {name || '—'}
      </span>
      {role && <span className="flex-none text-[12px] text-muted-foreground">{role}</span>}
      <span className="flex-none font-mono text-[11px] text-muted-foreground">media #{mediaId || '—'}</span>
    </div>
  );
}

/* ---------------------------------- main panel --------------------------------- */

function VisualIdentityPanelBody({
  canVerify,
  imageSource,
  legacyImageryValue = '',
}: VisualIdentityPanelBodyProps) {
  // The tenant this component rendered for: every write names it, and the store refuses one for a tenant it has left.
  const bbTenant = useBrandBibleWizardStore((s) => s.tenantId) ?? '';
  const visual = useSingleton('visual_identity', bbTenant);
  const tenantId = useBrandBibleWizardStore((s) => s.tenantId);
  const verifyEntryAction = useBrandBibleWizardStore((s) => s.verifyEntry);
  const unverifyEntryAction = useBrandBibleWizardStore((s) => s.unverifyEntry);
  const draftCommit = useBbDraftCommit();

  const [colorErrors, setColorErrors] = useState<Partial<Record<ColorTokenRole, string>>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  // The section-local edit buffer — the whole point of the conversion.
  const [buffer, setBuffer] = useState<HBuffer>({});

  // `fieldGen` remounts the UNCONTROLLED inputs (colour hex, logo
  // reference/usage-note, photo library) so a discard, a successful save, or a
  // programmatic styling fill re-reads their defaultValue. Operator keystrokes
  // never bump it (that would drop the un-blurred DOM value); only a
  // buffer-clearing or programmatic-fill event does.
  const [fieldGen, setFieldGen] = useState(0);

  // Badge-notes AI-suggest: a draft the operator must explicitly act on when the
  // field ALREADY holds their own policy (append / replace / keep). `null` = no
  // draft pending. Empty field never uses this — acceptBadgeSuggestion buffers
  // it straight in. Nothing here writes to the service; it only edits the buffer.
  const [pendingBadgeSuggestion, setPendingBadgeSuggestion] = useState<string | null>(null);

  const assetsTargetsRef = useRef<Map<string, HSuggestItem>>(new Map());
  const stylingTargetsRef = useRef<Map<string, HSuggestItem>>(new Map());
  const badgeTargetsRef = useRef<Map<string, HSuggestItem>>(new Map());
  const [assetsUi, setAssetsUi] = useState<SuggestUi>(EMPTY_SUGGEST_UI);
  const [stylingUi, setStylingUi] = useState<SuggestUi>(EMPTY_SUGGEST_UI);
  const [badgeUi, setBadgeUi] = useState<SuggestUi>(EMPTY_SUGGEST_UI);

  const entry = visual.entry;
  const payload: VisualIdentityPayload = (entry ?? {}) as VisualIdentityPayload;

  // Store-derived snapshots — the discard baseline AND the save diff basis
  // (snapshot-then-diff: only edited fields ever enter the patch).
  const storeLogoRefs = asLogoRefs(payload.logo_refs);
  const storeColorTokens = asColorTokens(payload.color_tokens);
  const storeFontNames = asFontNames(payload.font_names);
  const storePhotoLib = payload.photo_library_ref ?? '';
  const storeBadge = payload.badge_usage_notes ?? '';
  const storeImagery = legacyImageryValue;

  // Buffered display values: the buffer wins while present, else the store.
  const logoRefs = buffer.logoRefs ?? storeLogoRefs;
  const colorTokens: Partial<ColorTokens> = buffer.colorEdits
    ? { ...storeColorTokens, ...buffer.colorEdits }
    : storeColorTokens;
  const fontNames = buffer.fontNames ?? storeFontNames;
  const photoLibraryValue = buffer.photoLibraryRef ?? storePhotoLib;
  const badgeValue = buffer.badgeUsageNotes ?? storeBadge;
  const imageryValue = buffer.imagery ?? storeImagery;

  // real_photos_only is DERIVED from the image source (operator ruling: the
  // image source has the final say). Only "client's own assets" makes the
  // "only real photos" claim true; AI, stock, and hybrid are all false. Hybrid
  // is false ON PURPOSE — "only" is the whole claim, so if any AI image can
  // ship (hybrid) the record must not tell a writer every photo is the
  // client's own. An absent / not-yet-loaded source is UNKNOWN (null): derive
  // nothing and write nothing — never a guess.
  const derivedRealPhotosOnly: boolean | null =
    imageSource === null ? null : imageSource === 'client_assets';
  const sourceKnown = derivedRealPhotosOnly !== null;
  const storedRealPhotosOnly = entry?.real_photos_only ?? null;
  const displayRealPhotosOnly: boolean | null = sourceKnown
    ? derivedRealPhotosOnly
    : storedRealPhotosOnly;

  // Reconcile an EXISTING row whose stored value disagrees with the source.
  // This is a SYSTEM write (following the image source), NOT an operator edit,
  // so it stays immediate — it never routes through the buffer. Never fires on
  // an unknown source (that would be writing a guess) or before a row exists
  // (the first save injects the default instead).
  const reconciledKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!entry) return;
    if (derivedRealPhotosOnly === null) return;
    if (entry.real_photos_only === derivedRealPhotosOnly) return;
    const key = `${entry.id}:${String(derivedRealPhotosOnly)}`;
    if (reconciledKeyRef.current === key) return;
    reconciledKeyRef.current = key;
    void visual.save({ real_photos_only: derivedRealPhotosOnly });
  }, [entry, derivedRealPhotosOnly, visual.save]);

  /* ------------------------------- buffer setters ------------------------------ */

  const bufferScalar = useCallback(
    (key: 'photoLibraryRef' | 'badgeUsageNotes' | 'imagery', value: string, storeValue: string) => {
      setBuffer((prev) => {
        const next = { ...prev };
        if (value === storeValue) delete next[key];
        else next[key] = value;
        return next;
      });
    },
    [],
  );

  const bufferLogoRefs = (nextArr: LogoRef[]) => {
    setBuffer((prev) => {
      const next = { ...prev };
      if (logoRowsEqual(nextArr, storeLogoRefs)) delete next.logoRefs;
      else next.logoRefs = nextArr;
      return next;
    });
  };

  const bufferFonts = (nextArr: string[]) => {
    setBuffer((prev) => {
      const next = { ...prev };
      if (sameStringSet(nextArr, storeFontNames)) delete next.fontNames;
      else next.fontNames = nextArr;
      return next;
    });
  };

  const bufferColor = (role: ColorTokenRole, value: string) => {
    setBuffer((prev) => {
      const edits: Partial<Record<ColorTokenRole, string>> = { ...(prev.colorEdits ?? {}) };
      const storeVal = storeColorTokens[role] ?? '';
      if (value === storeVal) delete edits[role];
      else edits[role] = value;
      const next = { ...prev };
      if (Object.keys(edits).length > 0) next.colorEdits = edits;
      else delete next.colorEdits;
      return next;
    });
  };

  const commitColor = (role: ColorTokenRole, raw: string) => {
    const value = raw.trim();
    if (value && !isValidHex(value)) {
      setColorErrors((e) => ({ ...e, [role]: 'Enter a valid hex color, e.g. #1A2B3C.' }));
      return; // invalid — must not buffer
    }
    setColorErrors((e) => ({ ...e, [role]: undefined }));
    bufferColor(role, value);
  };

  const addLogoRef = () => {
    bufferLogoRefs([...logoRefs, { kind: 'primary', reference: '', usage_note: undefined }]);
  };
  const removeLogoRef = (i: number) => {
    bufferLogoRefs(logoRefs.filter((_, idx) => idx !== i));
  };
  const updateLogoRef = (i: number, rowPatch: Partial<LogoRef>) => {
    // Spread over the ORIGINAL row object so an untouched enrichment row keeps
    // its exact bytes and only the edited row changes.
    bufferLogoRefs(logoRefs.map((r, idx) => (idx === i ? { ...r, ...rowPatch } : r)));
  };

  /**
   * Approval is a plain column PATCH (upsertEntry), never the /verify
   * endpoint. Governance, not an edit — stays IMMEDIATE, never buffered.
   */
  const saveApproval = async (checked: boolean) => {
    try {
      await visual.save({ approved_for_public: checked } as Partial<Record<string, unknown>>);
      setSaveError(null);
    } catch {
      setSaveError("Couldn't save that change — try again.");
    }
  };

  /**
   * Accepting a badge suggestion NEVER clobbers policy the operator already
   * wrote. Empty field → buffer the draft straight in. Non-empty field → stage
   * the suggestion as a draft and let the operator explicitly append, replace,
   * or keep what they had. A suggestion merely APPEARING buffers nothing; only
   * these handlers touch the buffer. Only badge notes (prose) ever take
   * generated text — no fact field does. Nothing here writes to the service.
   */
  const applyBadge = (next: string) => bufferScalar('badgeUsageNotes', next, storeBadge);
  const acceptBadgeSuggestion = (text: string) => {
    if (!badgeValue.trim()) applyBadge(text);
    else setPendingBadgeSuggestion(text);
  };
  const appendBadgeSuggestion = () => {
    if (pendingBadgeSuggestion === null) return;
    const next = badgeValue.trim()
      ? `${badgeValue.trimEnd()}\n\n${pendingBadgeSuggestion}`
      : pendingBadgeSuggestion;
    setPendingBadgeSuggestion(null);
    applyBadge(next);
  };
  const replaceBadgeSuggestion = () => {
    if (pendingBadgeSuggestion === null) return;
    const next = pendingBadgeSuggestion;
    setPendingBadgeSuggestion(null);
    applyBadge(next);
  };
  const keepMineBadgeSuggestion = () => setPendingBadgeSuggestion(null);

  const setColorValues = recordedHexes(colorTokens);
  const logoPointers = recordedLogoPointers(logoRefs);
  const photoLibrary = photoLibraryValue.trim();

  /* ----------------------------- explicit-save wiring ---------------------------- */

  // Everything save()/discard()/changeCount()/isDirty() needs, read fresh from a
  // ref so the registered callbacks stay STABLE (they don't churn the registry)
  // yet always act on the latest buffer + store snapshot.
  const modelRef = useRef<{
    buffer: HBuffer;
    storeColorTokens: Partial<ColorTokens>;
    storeFontNames: string[];
    storeLogoRefs: LogoRef[];
    entry: BbEntry<'visual_identity'> | null;
    sourceKnown: boolean;
    derivedRealPhotosOnly: boolean | null;
    draftCommit: ReturnType<typeof useBbDraftCommit>;
    visualSave: typeof visual.save;
  }>({
    buffer,
    storeColorTokens,
    storeFontNames,
    storeLogoRefs,
    entry: entry ?? null,
    sourceKnown,
    derivedRealPhotosOnly,
    draftCommit,
    visualSave: visual.save,
  });
  modelRef.current = {
    buffer,
    storeColorTokens,
    storeFontNames,
    storeLogoRefs,
    entry: entry ?? null,
    sourceKnown,
    derivedRealPhotosOnly,
    draftCommit,
    visualSave: visual.save,
  };

  const isDirtyFn = useCallback(() => {
    const m = modelRef.current;
    return (
      computeChangeCount(m.buffer, { logoRefs: m.storeLogoRefs, fontNames: m.storeFontNames }) > 0
    );
  }, []);

  const changeCountFn = useCallback(() => {
    const m = modelRef.current;
    return computeChangeCount(m.buffer, { logoRefs: m.storeLogoRefs, fontNames: m.storeFontNames });
  }, []);

  const discard = useCallback(() => {
    setBuffer({});
    setColorErrors({});
    setPendingBadgeSuggestion(null);
    setSaveError(null);
    setFieldGen((g) => g + 1);
  }, []);

  const save = useCallback(async (): Promise<DirtyFormSaveResult> => {
    const m = modelRef.current;
    const b = m.buffer;

    // Assemble the v2 patch from ONLY the edited fields (snapshot-then-diff).
    // color_tokens overlays the edited roles onto the CURRENT store value and
    // nothing else — every role the client stored survives byte-identical, the
    // six contract roles AND any non-contract key (light, blue_gray, button, …).
    // No empty six-role skeleton is injected: doing so added background:''/
    // additional:'' for a tenant that never stored them, which the backend hex
    // check then rejected (an empty string is not a valid hex) — write-locking
    // §H colour saves for exactly those tenants. color_tokens is a FULL-REPLACE
    // column (not in JSONB_MERGE_COLUMNS), so the outbound object must be exactly
    // the intended stored shape — no spurious empty roles bloating it.
    const patch: Partial<VisualIdentityPayload> = {};
    if (b.colorEdits) patch.color_tokens = { ...m.storeColorTokens, ...b.colorEdits };
    if (b.fontNames) patch.font_names = b.fontNames;
    if (b.logoRefs) patch.logo_refs = b.logoRefs;
    if (b.photoLibraryRef !== undefined) patch.photo_library_ref = b.photoLibraryRef;
    if (b.badgeUsageNotes !== undefined) patch.badge_usage_notes = b.badgeUsageNotes;

    const hasV2 = Object.keys(patch).length > 0;
    const hasImagery = b.imagery !== undefined;
    if (!hasV2 && !hasImagery) return true;

    // The flat-field seam must exist BEFORE any write — a missing seam is a
    // hard, loud failure, never a silent drop of the buffered imagery. Checked
    // first so nothing is written when it cannot all be written.
    if (hasImagery && !m.draftCommit) {
      setSaveError(
        "Couldn't save the imagery field — the page save channel isn't available. Nothing was saved.",
      );
      return { success: false, error: 'flat-field commit seam (useBbDraftCommit) is unavailable' };
    }

    let v2Ok = true;
    if (hasV2) {
      // First save (no row yet) injects the DERIVED real_photos_only when the
      // source is known — same rule the old immediate path used.
      const isFirstSave = !m.entry;
      const withDefaults: Partial<VisualIdentityPayload> =
        isFirstSave && m.sourceKnown && patch.real_photos_only === undefined
          ? { real_photos_only: m.derivedRealPhotosOnly as boolean, ...patch }
          : patch;
      try {
        await m.visualSave(withDefaults);
      } catch {
        v2Ok = false;
      }
    }

    let flatOk = true;
    if (hasImagery && m.draftCommit) {
      try {
        flatOk = await m.draftCommit({ visual_identity: b.imagery }, 'H');
      } catch {
        flatOk = false;
      }
    }

    if (!v2Ok || !flatOk) {
      setSaveError("Couldn't save that change — try again.");
      return {
        success: false,
        error: !v2Ok ? 'visual identity save failed' : 'imagery save failed',
      };
    }

    // Committed: drop the buffer and remount the uncontrolled inputs so they
    // re-read the now-persisted store values.
    setBuffer({});
    setSaveError(null);
    setFieldGen((g) => g + 1);
    return true;
  }, []);

  const changeCountValue = computeChangeCount(buffer, {
    logoRefs: storeLogoRefs,
    fontNames: storeFontNames,
  });

  // A fresh registration object whenever the change count crosses a value —
  // that is exactly when the registry must notify (Save button enable/label,
  // Unsaved indicator, beforeunload). Edits that don't change the count need no
  // notify; the displayed state is already correct.
  const registration: BbSectionSaveRegistration = useMemo(
    () => ({ isDirty: isDirtyFn, save, discard, changeCount: changeCountFn }),
    [changeCountValue, isDirtyFn, save, discard, changeCountFn],
  );
  useRegisterBbSectionSave('H', registration);

  /* --------------------------------- AI suggest --------------------------------- */

  const runSuggest = async (
    setUi: (ui: SuggestUi) => void,
    targetsRef: { current: Map<string, HSuggestItem> },
    build: (dismissed: string[]) => ReturnType<typeof buildAssetsRequest>,
    toItem: (draft: FieldDraft) => HSuggestItem | null,
    dismissed: string[],
  ): Promise<{ items: string[]; source?: string }> => {
    setUi(EMPTY_SUGGEST_UI);
    const request = build(dismissed);
    try {
      const data = await fetchHSuggestions(request, tenantId, requestModelFieldSuggestions);
      const map = new Map<string, HSuggestItem>();
      const items: string[] = [];
      for (const draft of data.drafts ?? []) {
        const item = toItem(draft);
        if (!item || map.has(item.label)) continue;
        map.set(item.label, item);
        items.push(item.label);
      }
      targetsRef.current = map;
      setUi({
        refusals: data.refusals ?? [],
        errorKind: null,
        miss: false,
      });
      return { items, source: data.source || SUGGEST_SOURCE };
    } catch (err) {
      setUi({ refusals: [], errorKind: classifySuggestError(err), miss: false });
      throw err;
    }
  };

  const suggestAssets = ({ dismissed }: { existing: string[]; dismissed: string[] }) =>
    runSuggest(
      setAssetsUi,
      assetsTargetsRef,
      () =>
        buildAssetsRequest({
          logoRefs,
          photoLibraryRef: photoLibrary,
          dismissed,
        }),
      (draft) => itemFromAssetsDraft(draft, { refs: logoPointers, photo: photoLibrary }),
      dismissed,
    );

  const suggestStyling = ({ dismissed }: { existing: string[]; dismissed: string[] }) =>
    runSuggest(
      setStylingUi,
      stylingTargetsRef,
      () =>
        buildStylingRequest({
          colorTokens,
          fontNames,
          dismissed,
        }),
      (draft) =>
        itemFromStylingDraft(draft, {
          hexes: setColorValues,
          fonts: fontNames,
          emptyRoles: emptyColorRoles(colorTokens),
        }),
      dismissed,
    );

  const suggestBadge = ({ dismissed }: { existing: string[]; dismissed: string[] }) =>
    runSuggest(
      setBadgeUi,
      badgeTargetsRef,
      () =>
        buildBadgeRequest({
          logoKinds: logoRefs.map((r) => r.kind),
          realPhotosOnly: displayRealPhotosOnly,
          existingNotes: badgeValue,
          dismissed,
        }),
      (draft) => itemFromBadgeDraft(draft),
      dismissed,
    );

  const acceptMapped = (
    item: string,
    targetsRef: { current: Map<string, HSuggestItem> },
    setUi: (update: SuggestUi | ((prev: SuggestUi) => SuggestUi)) => void,
    apply: (mapped: HSuggestItem) => void,
  ) => {
    const mapped = targetsRef.current.get(item);
    if (!mapped) {
      setUi((prev) => ({ ...prev, miss: true }));
      return;
    }
    setUi((prev) => ({ ...prev, miss: false }));
    apply(mapped);
  };

  const acceptAssetsSuggestion = (item: string) =>
    acceptMapped(item, assetsTargetsRef, setAssetsUi, (mapped) => {
      if (mapped.target_field === 'logo_refs') {
        if (logoPointers.some((p) => p.toLowerCase() === mapped.value.toLowerCase())) return;
        // Buffered add — committed on Save, exactly like a manual "+ Add".
        bufferLogoRefs([...logoRefs, { kind: 'other', reference: mapped.value, usage_note: undefined }]);
      }
      // photo_library_ref: only a recorded pointer can pass the mapper, which
      // means it is already on the field — buffer nothing.
    });

  const acceptStylingSuggestion = (item: string) =>
    acceptMapped(item, stylingTargetsRef, setStylingUi, (mapped) => {
      if (mapped.target_field === 'color_tokens') {
        const empty = emptyColorRoles(colorTokens)[0];
        if (!empty) return;
        if (!setColorValues.some((hex) => hex.toLowerCase() === mapped.value.toLowerCase())) return;
        setColorErrors((e) => ({ ...e, [empty]: undefined }));
        // Buffer the fill, then remount the uncontrolled swatches so the newly
        // filled role shows its value straight away.
        bufferColor(empty, mapped.value);
        setFieldGen((g) => g + 1);
      }
      // font_names: mapper only passes recorded names, which are already on
      // the field — buffer nothing.
    });

  const acceptBadgeChip = (item: string) =>
    acceptMapped(item, badgeTargetsRef, setBadgeUi, (mapped) => {
      acceptBadgeSuggestion(mapped.value);
    });

  // The header for §H lives in BrandBiblePage, outside this panel — the
  // Verify-all affordance renders at the top of this panel's own body
  // instead, right-aligned, rather than reaching into that page.
  const pendingVisualItems: VerifyAllItem[] =
    entry && !entry.verified_by ? [{ table: 'visual_identity' as const, id: entry.id }] : [];

  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex justify-end">
        <VerifyAllButton items={pendingVisualItems} canVerify={canVerify} verifyEntry={verifyEntryAction} tenantId={bbTenant} />
      </div>
      {entry && (
        <RecordRail
          entry={entry}
          canVerify={canVerify}
          onVerify={() => void verifyEntryAction('visual_identity', entry.id, undefined, bbTenant)}
          onUnverify={() => void unverifyEntryAction('visual_identity', entry.id, bbTenant)}
          onApprovalChange={(checked) => void saveApproval(checked)}
        />
      )}

      {saveError && (
        <p role="alert" className="rounded-lg bg-destructive/10 px-3 py-2 text-[12px] text-destructive">
          {saveError}
        </p>
      )}

      {/* 1 — Logo references: repeatable rows, pointers only. Button 1 · Assets
          lives in THIS header (Fix 2) — it covers logo_refs + photo_library_ref,
          so an operator looking to add a logo finds the control right where they
          are, not over in the Photo library card. */}
      <StepCard
        title="Logos"
        headerRight={
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">pointers only — no uploads here</span>
              <AISuggest
                sectionId={H_SECTION_IDS.assets}
                kind="chips"
                placeholder="Suggest assets"
                source={SUGGEST_SOURCE}
                existing={[]}
                fetcher={suggestAssets}
                onAccept={acceptAssetsSuggestion}
                maxShow={12}
              />
            </div>
            <HSuggestChrome notes={[ASSETS_POINTER_NOTE]} ui={assetsUi} />
          </div>
        }
      >
        <div className="flex flex-col gap-2 p-4">
          {logoRefs.map((row, i) =>
            // Enrichment-shaped rows ({name, role, media_id}) render READ-ONLY
            // and are excluded from every editable/buffered action (PR #1624) —
            // see EnrichmentLogoRow and the compat block near asLogoRefs. They
            // sit untouched in the buffer array, so a buffered Save carries them
            // through byte-identical. Canonical (and any other degraded) rows
            // keep the full editable affordance below, now buffered.
            isEnrichmentLogoRef(row) ? (
              <EnrichmentLogoRow key={`logo-${i}-${fieldGen}`} index={i} row={row} />
            ) : (
              <div
                key={`logo-${i}-${fieldGen}`}
                className="flex items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-1.5"
              >
                {/* kind — small grey pill (still a select, so no capability is lost).
                    `?? 'other'` keeps the select CONTROLLED for a degraded row that
                    is missing `kind` (never flips controlled→uncontrolled). */}
                <select
                  className="flex-none appearance-none rounded-full border border-border bg-teal-light px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-teal outline-none focus:border-coral"
                  value={row.kind ?? 'other'}
                  onChange={(e) => updateLogoRef(i, { kind: e.target.value as LogoRefKind })}
                  aria-label={`Logo kind for row ${i + 1}`}
                >
                  {LOGO_KIND_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {/* reference — monospace, the primary pointer. `?? ''` guards a
                    degraded row with no `reference` key. */}
                <input
                  className="min-w-0 flex-[2] bg-transparent font-mono text-[12.5px] text-foreground outline-none placeholder:text-muted-foreground/50"
                  defaultValue={row.reference ?? ''}
                  placeholder="https://example.com/logo.svg"
                  aria-label={`Logo reference for row ${i + 1}`}
                  onBlur={(e) => {
                    if (e.target.value !== (row.reference ?? '')) updateLogoRef(i, { reference: e.target.value });
                  }}
                />
                {/* usage note — kept editable inline so no capability is dropped */}
                <input
                  className="min-w-0 flex-1 bg-transparent text-[12px] text-muted-foreground outline-none placeholder:text-muted-foreground/40"
                  defaultValue={row.usage_note ?? ''}
                  placeholder="usage note (optional)"
                  aria-label={`Usage note for row ${i + 1}`}
                  onBlur={(e) => {
                    const next = e.target.value.trim();
                    if (next !== (row.usage_note ?? '')) updateLogoRef(i, { usage_note: next || undefined });
                  }}
                />
                <button
                  type="button"
                  aria-label={`Remove logo reference row ${i + 1}`}
                  onClick={() => removeLogoRef(i)}
                  className="flex-none text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </div>
            ),
          )}
          <button
            type="button"
            onClick={addLogoRef}
            className="self-start rounded-lg border border-dashed border-input px-3.5 py-2 text-xs font-medium text-teal hover:border-coral hover:text-coral-dark"
          >
            + Add logo reference
          </button>
        </div>
      </StepCard>

      {/* 2 — Color tokens: exactly six roles. Button 2 · Styling lives in this
          header and covers color_tokens + font_names. */}
      <StepCard
        title="Color tokens"
        headerRight={
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">one chip per role · set a hex to fill the swatch</span>
              <AISuggest
                sectionId={H_SECTION_IDS.styling}
                kind="chips"
                placeholder="Suggest styling"
                source={SUGGEST_SOURCE}
                existing={[]}
                fetcher={suggestStyling}
                onAccept={acceptStylingSuggestion}
                maxShow={12}
              />
            </div>
            <HSuggestChrome ui={stylingUi} />
          </div>
        }
      >
        <div className="p-4">
          <div className="flex flex-wrap gap-2.5">
            {COLOR_ROLES.map(({ role, label }) => (
              <ColorRoleChip
                key={`${role}-${fieldGen}`}
                role={role}
                label={label}
                value={colorTokens[role] ?? ''}
                error={colorErrors[role]}
                onCommit={commitColor}
              />
            ))}
          </div>
          {setColorValues.length === 0 && (
            <NoGeneratedValueNote>
              No brand colour is on file yet. Colours come from the client’s own
              assets — enter them here. AI will not invent a hex.
            </NoGeneratedValueNote>
          )}
        </div>
      </StepCard>

      {/* Fonts (chip list) and Photo library (single pointer) share one row */}
      <div className="grid grid-cols-2 gap-3.5">
        <StepCard title="Fonts" headerRight={<span className="text-xs text-muted-foreground">names only, no font files</span>}>
          <div className="p-4">
            <div>
              <SmartTagInput
                value={fontNames}
                onChange={(next) => bufferFonts(next)}
                placeholder="e.g. Open Sans"
                bulkPasteEnabled={false}
              />
            </div>
            {fontNames.length === 0 && (
              <NoGeneratedValueNote>
                No typefaces on file yet. Fonts are the client’s own — enter them
                here. AI will not invent a typeface.
              </NoGeneratedValueNote>
            )}
          </div>
        </StepCard>

        {/* The Assets suggest control (logo_refs + photo_library_ref) now lives
            in the Logos card header (Fix 2), not here — it covers this card's
            field too, but reads naturally beside the logo rows. */}
        <StepCard title="Photo library">
          <div className="p-4">
            <Field label="Photo library reference" hint="pointer only">
              <input
                key={`photo-lib-${fieldGen}`}
                className={inputClass}
                defaultValue={photoLibraryValue}
                placeholder="Where the client's photos live"
                onBlur={(e) => {
                  if (e.target.value !== photoLibraryValue)
                    bufferScalar('photoLibraryRef', e.target.value, storePhotoLib);
                }}
              />
              <p className="text-[11px] text-muted-foreground">
                Managing the library itself happens in Image Assets, not here.
              </p>
            </Field>
          </div>
        </StepCard>
      </div>

      {/* Real photos only — DERIVED, read-only. The image source has the final
          say (operator ruling); this is no longer a hand-editable toggle. It
          still round-trips as a stored field, and both writers and reviewers
          read it. */}
      <StepCard
        title="Real photos only"
        headerRight={<span className="text-xs text-muted-foreground">derived · read-only</span>}
      >
        <div className="flex items-start gap-3 p-4">
          <div className="flex-1">
            <div
              className="text-[13px] font-medium text-foreground"
              data-testid="real-photos-only-readonly"
            >
              Real photos only ·{' '}
              {displayRealPhotosOnly === null ? 'not set' : displayRealPhotosOnly ? 'yes' : 'no'}
            </div>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              {sourceKnown
                ? 'Follows the image source — writers and reviewers read this. Not editable here.'
                : 'The image source hasn’t loaded, so this shows the stored value and isn’t being changed. Writers and reviewers read this.'}{' '}
              {REAL_PHOTOS_NO_SUGGEST}
            </p>
          </div>
          <EnvelopeBadge kind="source">read-only</EnvelopeBadge>
        </div>
      </StepCard>

      {/* 5 — Badge usage notes: the only prose field in §H. The AI-suggest
          control sits beside THIS field and proposes a DRAFT the operator
          edits; it never silently overwrites policy already written. */}
      <StepCard
        title="Badge usage notes"
        headerRight={
          <div className="flex flex-col items-end gap-1">
            <AISuggest
              sectionId={H_SECTION_IDS.badge}
              kind="text"
              placeholder="Suggest badge wording"
              source={SUGGEST_SOURCE}
              existing={badgeValue ? [badgeValue] : []}
              fetcher={suggestBadge}
              onAcceptText={acceptBadgeChip}
            />
            <HSuggestChrome ui={badgeUi} />
          </div>
        }
      >
        <div className="p-4">
          <textarea
            className={cn(inputClass, 'min-h-[72px] resize-y')}
            value={badgeValue}
            placeholder="e.g. Minimum size, placement rules, or usage restrictions"
            onChange={(e) => bufferScalar('badgeUsageNotes', e.target.value, storeBadge)}
          />
          {/* Non-empty field: the accepted suggestion is a DRAFT staged here,
              not a replacement. The operator's own notes stay in the textarea
              above until they explicitly append, replace, or keep them. */}
          {pendingBadgeSuggestion !== null && (
            <div className="mt-2.5 rounded-xl border border-teal/30 bg-teal-light p-3">
              <p className="mb-1.5 text-[11.5px] font-semibold text-teal-dark">
                Suggested draft — your current notes are kept until you choose
              </p>
              <p className="mb-2.5 whitespace-pre-wrap text-[13px] leading-relaxed text-foreground">
                {pendingBadgeSuggestion}
              </p>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={appendBadgeSuggestion}
                  className="rounded-md border border-coral bg-coral px-2.5 py-1 text-[11.5px] font-semibold text-white hover:bg-coral-hover"
                >
                  Append to my notes
                </button>
                <button
                  type="button"
                  onClick={replaceBadgeSuggestion}
                  className="rounded-md border border-border bg-warm-bg px-2.5 py-1 text-[11.5px] font-semibold text-coral-dark hover:bg-coral-light"
                >
                  Replace my notes
                </button>
                <button
                  type="button"
                  onClick={keepMineBadgeSuggestion}
                  className="rounded-md border border-border bg-transparent px-2.5 py-1 text-[11.5px] font-medium text-muted-foreground hover:text-foreground"
                >
                  Keep mine
                </button>
              </div>
            </div>
          )}
        </div>
      </StepCard>

      {/* 6 — Imagery & references: the legacy `visual_identity` flat column,
          not a bb_visual_identity field. It buffers with the rest and commits
          on Save through the page's useBbDraftCommit() seam (see file header). */}
      <StepCard
        title="Imagery & references"
        headerRight={<span className="text-xs text-muted-foreground">legacy field — no v2 column yet</span>}
      >
        <div className="p-4">
          <textarea
            className={cn(inputClass, 'min-h-[72px] resize-y')}
            value={imageryValue}
            placeholder="Photo style, lighting, mood…"
            onChange={(e) => bufferScalar('imagery', e.target.value, storeImagery)}
          />
        </div>
      </StepCard>
    </div>
  );
}

/* ----------------------- optional tenant-settings read ----------------------- */

/**
 * The message useAuth (contexts/AuthContext) throws when it runs outside an
 * AuthProvider. We can't import the raw AuthContext to read it non-throwingly —
 * it isn't exported — and editing that file is out of scope, so the graceful
 * "is a provider present?" read is done by catching THIS exact throw (and only
 * this one) in the boundary below.
 */
const MISSING_AUTH_PROVIDER_MESSAGE = 'useAuth must be used within AuthProvider';

/**
 * Reads real_photos_only's SOURCE-OF-TRUTH by REUSING the exact hook
 * ImageSourceSettings uses — same useTenantSettings / React Query key
 * (['tenantSettings', website]); no new API call, the cache is shared.
 * `tenantId` is the same domain string BrandBiblePage passes to
 * ImageSourceSettings as `website` (bridge pattern: tenant_id == website).
 * useTenantSettings calls useAuth, which THROWS outside an AuthProvider — that
 * throw is handled one level up (see VisualIdentityPanel).
 */
function VisualIdentityPanelWithSettings(props: VisualIdentityPanelProps) {
  const tenantId = useBrandBibleWizardStore((s) => s.tenantId);
  const { data: tenantSettings } = useTenantSettings(tenantId);
  return (
    <VisualIdentityPanelBody {...props} imageSource={tenantSettings?.image_source ?? null} />
  );
}

/**
 * Catches ONLY the "no AuthProvider" throw from the settings read and renders
 * the supplied fallback (the body with an unknown image source); any other
 * error is rethrown so genuine failures still surface instead of being masked.
 */
class OptionalTenantSettingsBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    const { error } = this.state;
    if (error) {
      if (error.message === MISSING_AUTH_PROVIDER_MESSAGE) return this.props.fallback;
      throw error; // not ours — let a real error propagate to the next boundary
    }
    return this.props.children;
  }
}

/**
 * §H Visual Identity panel.
 *
 * WHY THE SPLIT: deriving real_photos_only means reading the tenant image
 * source through useTenantSettings, which calls useAuth and THROWS when there
 * is no AuthProvider. BrandBiblePage mounts every A–I section at once, and its
 * sibling page suites render that page WITHOUT an AuthProvider — a hard
 * dependency here detonated four of them. So the read is OPTIONAL: with a
 * provider the body gets the real image source; without one the boundary
 * catches useAuth's throw and the body renders with an unknown source, showing
 * the existing cannot-derive / "not set" state instead of crashing.
 *
 * The Rules of Hooks forbid calling useTenantSettings conditionally, so the
 * shape is a component split: an inner component that ALWAYS calls the hook
 * (VisualIdentityPanelWithSettings) versus a fallback body that never does —
 * each renders its own hooks unconditionally. The provider is detected by
 * catching the throw rather than by useContext(AuthContext), because that
 * context object is not exported and editing AuthContext.tsx is out of scope.
 */
export function VisualIdentityPanel(props: VisualIdentityPanelProps) {
  return (
    <OptionalTenantSettingsBoundary
      fallback={<VisualIdentityPanelBody {...props} imageSource={null} />}
    >
      <VisualIdentityPanelWithSettings {...props} />
    </OptionalTenantSettingsBoundary>
  );
}
